import { FC, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  Box,
  Alert,
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { inventoryService, StockLocation, TransferPayload } from '../services/inventoryService';
import QuantityField from './QuantityField';
import { formatQuantity, roundQuantity } from '../utils/quantity';

// Minimal shape TransferDialog needs from an Inventory list row — a subset of
// the Inventory page's local `InventoryItem` (Task 8: locationId; Task 9a:
// catalogItemId, the by-convention FK to ServiceCatalog). The backend's
// POST /inventory/transfer (Task 5) resolves the FROM row by
// `(clinicId, catalogItemId, locationId)`, so ONLY catalog-linked (priced)
// items are addressable by this endpoint — an item with no catalogItemId
// can't be transferred, hence the filter in `fromLocationItems` below.
export interface TransferableItem {
  id: string;
  name: string;
  quantity: number;
  locationId?: string;
  catalogItemId?: string;
}

interface TransferDialogProps {
  open: boolean;
  onClose: () => void;
  locations: StockLocation[];
  items: TransferableItem[];
  // The clinic's default warehouse — used to default "From location".
  defaultLocationId?: string;
  // Task 11 (trucks): optional pre-fill for the Inventory page's "Transfer to
  // par" row action — opens this SAME dialog with To/Item/Quantity already
  // set to close a below-par shortfall. All undefined (Task 10's manual
  // "Transfer stock" button) preserves the original from-scratch defaults.
  initialToLocationId?: string;
  initialCatalogItemId?: string;
  initialQuantity?: number;
}

/**
 * "Transfer stock" dialog (Task 10, trucks): moves on-hand stock of ONE
 * catalog-linked item from one StockLocation to another (e.g. warehouse -> a
 * vet's truck). Online-only, manage:inventory — the Inventory page's header
 * button gates entry (RBAC + connectivity, same convention as Task 9's
 * LocationsPanel writes); this dialog assumes those checks already passed.
 *
 * v1 is single line item (multi-line transfer cart deferred — see plan).
 */
const TransferDialog: FC<TransferDialogProps> = ({
  open,
  onClose,
  locations,
  items,
  defaultLocationId,
  initialToLocationId,
  initialCatalogItemId,
  initialQuantity,
}) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [selectedItem, setSelectedItem] = useState<TransferableItem | null>(null);
  const [quantity, setQuantity] = useState<number | ''>(1);
  const [formError, setFormError] = useState<string | null>(null);

  // Reset to sensible defaults every time the dialog is opened (not on every
  // locations/items refresh, which would clobber an in-progress selection).
  // Task 11: when the caller supplies initial* (the "Transfer to par" row
  // action), they win over the plain from-scratch defaults below.
  useEffect(() => {
    if (!open) return;
    const from = defaultLocationId || locations[0]?.id || '';
    const to =
      initialToLocationId ||
      locations.find((loc) => loc.type === 'TRUCK' && loc.id !== from)?.id ||
      locations.find((loc) => loc.id !== from)?.id ||
      '';
    const prefilledItem = initialCatalogItemId
      ? items.find((item) => item.catalogItemId === initialCatalogItemId && item.locationId === from) ?? null
      : null;
    setFromLocationId(from);
    setToLocationId(to);
    setSelectedItem(prefilledItem);
    setQuantity(initialQuantity && initialQuantity > 0 ? initialQuantity : 1);
    setFormError(null);
    // Only re-run when the dialog opens — locations/items/initial* are read
    // at that moment, not re-synced on every background refetch while it's open.
  }, [open]); // eslint-disable-line

  // Items available AT the selected From location — only catalog-linked
  // (priced) items are transferable, see TransferableItem's comment above.
  const fromLocationItems = useMemo(
    () => items.filter((item) => !!fromLocationId && item.locationId === fromLocationId && !!item.catalogItemId),
    [items, fromLocationId],
  );

  const toLocationOptions = useMemo(
    () => locations.filter((loc) => loc.id !== fromLocationId),
    [locations, fromLocationId],
  );

  const handleFromLocationChange = (locationId: string) => {
    setFromLocationId(locationId);
    setSelectedItem(null);
    setQuantity(1);
    setFormError(null);
    if (toLocationId === locationId) {
      setToLocationId(locations.find((loc) => loc.id !== locationId)?.id || '');
    }
  };

  const transferMutation = useMutation(
    (payload: TransferPayload) => inventoryService.transfer(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['inventory']);
        queryClient.invalidateQueries(['inventory-stats']);
        // Harmless: transfers don't change locations themselves, but keeping
        // this fresh costs nothing and matches LocationsPanel's convention.
        queryClient.invalidateQueries(['stock-locations']);
        enqueueSnackbar('Stock transferred', { variant: 'success' });
        onClose();
      },
      onError: (error: any) => {
        setFormError(error.response?.data?.message || 'Failed to transfer stock');
      },
    },
  );

  // Over-draw is refused server-side with a 400 (2026-08-21) — surface the
  // bound here so the user sees it before submitting.
  const exceedsOnHand =
    !!selectedItem && typeof quantity === 'number' && quantity > roundQuantity(selectedItem.quantity ?? 0);

  const isValid =
    !!fromLocationId &&
    !!toLocationId &&
    fromLocationId !== toLocationId &&
    !!selectedItem?.catalogItemId &&
    typeof quantity === 'number' &&
    quantity > 0 &&
    !exceedsOnHand;

  const handleSubmit = () => {
    if (!isValid || !selectedItem?.catalogItemId) return;
    setFormError(null);
    transferMutation.mutate({
      fromLocationId,
      toLocationId,
      catalogItemId: selectedItem.catalogItemId,
      quantity: quantity as number,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Transfer stock</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {formError && <Alert severity="error">{formError}</Alert>}

          <TextField
            select
            label="From location"
            value={fromLocationId}
            onChange={(e) => handleFromLocationChange(e.target.value)}
            fullWidth
            required
          >
            {locations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
            ))}
          </TextField>

          <Autocomplete
            options={fromLocationItems}
            value={selectedItem}
            onChange={(_, item) => {
              setSelectedItem(item);
              setQuantity(1);
            }}
            getOptionLabel={(item) => `${item.name} (${formatQuantity(item.quantity)} on hand)`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText="No priced items at this location"
            disabled={!fromLocationId}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Item"
                required
                helperText="Only priced items (linked to the price list) can be transferred"
              />
            )}
          />

          <TextField
            select
            label="To location"
            value={toLocationId}
            onChange={(e) => setToLocationId(e.target.value)}
            fullWidth
            required
            disabled={!fromLocationId}
          >
            {toLocationOptions.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
            ))}
          </TextField>

          <QuantityField
            label="Quantity"
            value={quantity === '' ? null : quantity}
            onChange={(next) => setQuantity(next ?? '')}
            error={exceedsOnHand}
            helperText={selectedItem ? (exceedsOnHand ? `Only ${formatQuantity(selectedItem.quantity)} available at source — the server refuses an over-draw` : `${formatQuantity(selectedItem.quantity)} available at source`) : undefined}
            fullWidth
            required
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={transferMutation.isLoading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isValid || transferMutation.isLoading}
        >
          Transfer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferDialog;
